import { useCallback } from "react";
import { Joyride, STATUS } from "react-joyride";
import { useTranslation } from "react-i18next";

export const TOUR_STORAGE_KEY = "dlc_boq_tour_seen";

/**
 * Guided walkthrough for the BoQ editor.
 *
 * Props
 * ─────
 * run       {boolean}    – controlled externally; true = tour is active
 * onFinish  {Function}   – called when the tour ends (completed or skipped)
 *
 * NOTE: Mount with a changing `key` prop each time you want to reset to step 0,
 * e.g. <BoQEditorTour key={tourKey} run={runTour} onFinish={...} />
 */
const BoQEditorTour = ({ run, onFinish }) => {
    const { t } = useTranslation();

    const steps = [
        {
            target: "body",
            placement: "center",
            disableBeacon: true,
            title: t("boq.tour.step-welcome-title"),
            content: t("boq.tour.step-welcome-content"),
        },
        {
            target: "[data-tour='boq-add']",
            placement: "bottom-start",
            disableBeacon: true,
            title: t("boq.tour.step-add-title"),
            content: t("boq.tour.step-add-content"),
        },
        {
            target: "[data-tour='boq-edit-actions']",
            placement: "bottom",
            disableBeacon: true,
            title: t("boq.tour.step-edit-title"),
            content: t("boq.tour.step-edit-content"),
        },
        {
            target: "[data-tour='boq-define-schema']",
            placement: "bottom",
            disableBeacon: true,
            title: t("boq.tour.step-schema-title"),
            content: t("boq.tour.step-schema-content"),
        },
        {
            target: "[data-tour='boq-tour-trigger']",
            placement: "bottom-end",
            disableBeacon: true,
            title: t("boq.tour.step-help-title"),
            content: t("boq.tour.step-help-content"),
        },
    ];

    const handleJoyrideCallback = useCallback(
        (data) => {
            const { status } = data;
            if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
                localStorage.setItem(TOUR_STORAGE_KEY, "true");
                onFinish?.();
            }
        },
        [onFinish],
    );

    return (
        <Joyride
            run={run}
            steps={steps}
            continuous
            showProgress
            showSkipButton
            disableScrolling
            spotlightClicks={false}
            callback={handleJoyrideCallback}
            locale={{
                back: t("boq.tour.btn-back"),
                close: t("boq.tour.btn-close"),
                last: t("boq.tour.btn-last"),
                next: t("boq.tour.btn-next"),
                skip: t("boq.tour.btn-skip"),
            }}
            styles={{
                options: {
                    primaryColor: "#4f46e5",
                    zIndex: 10000,
                },
                tooltip: {
                    borderRadius: 10,
                    fontSize: "0.875rem",
                },
                tooltipTitle: {
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                },
                buttonSkip: {
                    color: "#64748b",
                },
            }}
        />
    );
};

export default BoQEditorTour;

